import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Bell,
  ChevronRight,
  Crosshair,
  MapPin,
  Navigation,
  Radio,
  Route,
  ShieldCheck,
  Smartphone,
  UserPlus,
  Users,
  Waypoints,
  Wifi,
  Zap,
} from 'lucide-react';
import { api } from '@/services/api';
import { ActivityFeed } from '@/components/ui/activity-feed';
import { LiveMap } from '@/components/maps/live-map';
import { MiniChart } from '@/components/charts/mini-chart';
import { Skeleton } from '@/components/ui/skeleton';
import { useLiveLocations } from '@/hooks/use-live-locations';

function useCommandClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  return {
    time: new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).format(now),
    date: new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(now),
  };
}

function useTelemetrySeries(value, sampleKey) {
  const [samples, setSamples] = useState(() => [Number(value) || 0, Number(value) || 0]);
  useEffect(() => {
    setSamples((current) => [...current, Number(value) || 0].slice(-24));
  }, [sampleKey, value]);
  return samples;
}

export function OverviewPage() {
  const navigate = useNavigate();
  const clock = useCommandClock();
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => api('/api/v1/admin/overview').then((result) => result.data),
    refetchInterval: 15_000,
  });
  const activity = useQuery({
    queryKey: ['activity-feed'],
    queryFn: () => api('/api/v1/admin/activity-feed').then((result) => result.data),
    refetchInterval: 12_000,
  });
  const { locations, realtimeConnected, error: liveLocationError } = useLiveLocations();
  const currentLocationRate = Number(overview.data?.realtime?.locationEventsPerMinute) || 0;
  const chartValues = useTelemetrySeries(currentLocationRate, overview.dataUpdatedAt);

  if (overview.isLoading) return <OverviewSkeleton />;
  if (!overview.data) {
    return <div className="grid h-full place-items-center bg-rose-400/5 text-sm text-rose-200">
      {overview.error?.message ?? 'Overview unavailable'}
    </div>;
  }

  const d = overview.data;
  const metrics = [
    { label: 'Online riders', value: d.members.online, total: d.members.total, icon: Radio, tone: 'cyan' },
    { label: 'Active convoy', value: d.trips.active, total: d.trips.completed, icon: Waypoints, tone: 'violet' },
    { label: 'Live voice', value: d.voiceRooms.active, total: 'rooms', icon: Activity, tone: 'cyan' },
    { label: 'Tracked devices', value: d.devices?.total ?? 0, total: d.sessions?.active ?? 0, icon: Smartphone, tone: 'green' },
    { label: 'Location/min', value: d.realtime.locationEventsPerMinute, total: `${d.realtime.staleLocations ?? 0} stale`, icon: Crosshair, tone: 'amber' },
    { label: 'Security state', value: d.security?.open ?? 0, total: 'open', icon: ShieldCheck, tone: (d.security?.open ?? 0) > 0 ? 'rose' : 'green' },
  ];


  return <section className="command-overview">
    <header className="command-hero cyber-cut">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[8px] font-black uppercase tracking-[.24em] text-cyan-300">
          <span className="cyber-pulse" /> Live convoy operations
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-3">
          <h2 className="truncate text-lg font-black tracking-[-.035em] text-white xl:text-xl">Convoy Operations Center</h2>
          <span className="hidden border border-emerald-300/30 bg-emerald-300/8 px-2 py-1 text-[8px] font-black uppercase tracking-[.2em] text-emerald-300 md:inline-flex">System nominal</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="hidden min-w-28 border-l border-cyan-300/20 pl-4 text-right lg:block">
          <p className="font-mono text-base font-black leading-none text-cyan-200">{clock.time}</p>
          <p className="mt-1 text-[8px] font-bold uppercase tracking-[.18em] text-slate-600">{clock.date}</p>
        </div>
        <CommandButton icon={Bell} label="Broadcast" onClick={() => navigate('/notifications')} />
        <CommandButton icon={UserPlus} label="Add rider" primary onClick={() => navigate('/members')} />
      </div>
    </header>

    <div className="command-metric-rail">
      {metrics.map((metric, index) => <MetricCell key={metric.label} {...metric} index={index + 1} />)}
    </div>

    <div className="command-main-grid">
      <div className="cyber-panel command-map-panel min-h-0">
        <LiveMap locations={locations} className="absolute inset-0 h-full w-full" />
        <div className="command-map-scan" />
        <div className="absolute left-2.5 top-2.5 z-10 flex items-center gap-2 border border-cyan-300/25 bg-[#03070d]/88 px-2.5 py-1.5 backdrop-blur-xl">
          <Navigation size={12} className="text-cyan-300" />
          <div>
            <p className="text-[7px] font-black uppercase tracking-[.18em] text-cyan-300">Live tactical map</p>
            <p className="text-[9px] font-semibold text-slate-300">{locations.length} rider signals · {d.trips.active} active convoy · {realtimeConnected ? 'socket live' : 'recovering'}</p>
          </div>
        </div>
        <button onClick={() => navigate('/live-monitoring')} className="absolute right-2.5 top-2.5 z-10 flex h-8 items-center gap-1.5 border border-white/12 bg-[#03070d]/88 px-2.5 text-[8px] font-black uppercase tracking-[.16em] text-slate-300 backdrop-blur-xl transition hover:border-cyan-300/40 hover:text-cyan-200">
          Full map <ChevronRight size={12} />
        </button>
        {liveLocationError && <div className="absolute bottom-12 left-2.5 z-10 border border-rose-300/25 bg-[#12070b]/92 px-2.5 py-1.5 text-[8px] font-bold text-rose-200">{liveLocationError.message}</div>}
        <div className="command-map-footer">
          <MapSignal label="Socket" value={d.realtime.socketConnections} icon={Wifi} />
          <MapSignal label="Riders" value={d.members.online} icon={Users} />
          <MapSignal label="Stale" value={d.realtime.staleLocations ?? 0} icon={MapPin} warning={(d.realtime.staleLocations ?? 0) > 0} />
          <MapSignal label="Routes" value={d.trips.active} icon={Route} />
        </div>
      </div>

      <div className="command-side-stack min-h-0">
        <div className="cyber-panel min-h-0 overflow-hidden">
          <PanelTitle eyebrow="Telemetry stream" title="Network Pulse" action={<span className="text-[7px] font-black uppercase tracking-[.16em] text-emerald-300">● Live</span>} />
          <div className="grid h-[calc(100%-42px)] min-h-0 grid-cols-[1fr_76px] gap-2 p-2">
            <div className="min-h-0 overflow-hidden border border-white/6 bg-black/20">
              <MiniChart label="Location events" values={chartValues} compact unit="/min" />
            </div>
            <div className="grid min-h-0 grid-rows-3 gap-2">
              <PulseValue label="Links" value={d.realtime.socketConnections} />
              <PulseValue label="Voice" value={d.voiceRooms.active} />
              <PulseValue label="Events" value={d.realtime.locationEventsPerMinute} />
            </div>
          </div>
        </div>

        <div className="cyber-panel flex min-h-0 flex-col overflow-hidden">
          <PanelTitle eyebrow="Event channel" title="Live Activity" action={<button className="text-[7px] font-black uppercase tracking-[.16em] text-cyan-300" onClick={() => activity.refetch()}>Refresh</button>} />
          <div className="scrollbar min-h-0 flex-1 overflow-y-auto p-1.5">
            {activity.isLoading
              ? <div className="space-y-2 p-2">{Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-12" />)}</div>
              : <ActivityFeed items={(activity.data ?? []).slice(0, 8)} compact />}
          </div>
        </div>
      </div>
    </div>

    <div className="command-bottom-grid">
      <div className="cyber-panel overflow-hidden">
        <PanelTitle eyebrow="Convoy matrix" title="Fleet State" />
        <div className="grid h-[calc(100%-42px)] grid-cols-4 divide-x divide-white/7">
          <StateValue label="Active" value={d.trips.active} tone="cyan" />
          <StateValue label="Draft" value={d.trips.draft ?? 0} />
          <StateValue label="Complete" value={d.trips.completed} tone="green" />
          <StateValue label="Cancelled" value={d.trips.cancelled ?? 0} tone="rose" />
        </div>
      </div>

      <div className="cyber-panel overflow-hidden">
        <PanelTitle eyebrow="Access matrix" title="Rider State" />
        <div className="grid h-[calc(100%-42px)] grid-cols-4 divide-x divide-white/7">
          <StateValue label="Active" value={d.members.active} tone="green" />
          <StateValue label="Awaiting Google" value={d.members.pendingGoogleLink ?? d.members.pendingVerification} tone="amber" />
          <StateValue label="Disabled" value={d.members.disabled} />
          <StateValue label="Blocked" value={d.members.blocked} tone="rose" />
        </div>
      </div>

      <div className="cyber-panel overflow-hidden">
        <PanelTitle eyebrow="Direct access" title="Quick Commands" />
        <div className="grid h-[calc(100%-42px)] grid-cols-4 divide-x divide-white/7">
          <QuickCommand label="Map" icon={MapPin} onClick={() => navigate('/live-monitoring')} />
          <QuickCommand label="Convoys" icon={Route} onClick={() => navigate('/convoys')} />
          <QuickCommand label="Riders" icon={Users} onClick={() => navigate('/members')} />
          <QuickCommand label="Health" icon={Zap} onClick={() => navigate('/system-health')} />
        </div>
      </div>
    </div>
  </section>;
}

function CommandButton({ icon: Icon, label, onClick, primary = false }) {
  return <button onClick={onClick} className={`cyber-button hidden h-9 items-center gap-2 px-3 text-[8px] font-black uppercase tracking-[.16em] sm:flex ${primary ? 'cyber-button-primary' : ''}`}>
    <Icon size={13} /> {label}
  </button>;
}

function MetricCell({ label, value, total, icon: Icon, tone, index }) {
  return <div className={`command-metric tone-${tone}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[6px] font-black uppercase tracking-[.14em] text-slate-600">0{index} / {label}</p>
        <p className="mt-1 font-mono text-lg font-black leading-none text-white">{value ?? 0}</p>
      </div>
      <Icon size={16} className="metric-icon" />
    </div>
    <div className="mt-1.5 flex items-center justify-between text-[7px] font-bold uppercase tracking-[.14em] text-slate-600">
      <span>{total}</span><span className="metric-line" />
    </div>
  </div>;
}

function PanelTitle({ eyebrow, title, action }) {
  return <div className="flex h-[42px] items-center justify-between border-b border-white/7 px-3">
    <div><p className="text-[6px] font-black uppercase tracking-[.2em] text-cyan-300">{eyebrow}</p><h3 className="mt-0.5 text-[10px] font-black uppercase tracking-[.07em] text-slate-100">{title}</h3></div>
    {action}
  </div>;
}

function MapSignal({ label, value, icon: Icon, warning = false }) {
  return <div className="flex min-w-0 items-center gap-2 border-r border-white/8 px-3 last:border-r-0">
    <Icon size={12} className={warning ? 'text-amber-300' : 'text-cyan-300'} />
    <div className="min-w-0"><p className="font-mono text-xs font-black leading-none text-white">{value}</p><p className="mt-1 truncate text-[6px] font-black uppercase tracking-[.14em] text-slate-600">{label}</p></div>
  </div>;
}

function PulseValue({ label, value }) {
  return <div className="grid min-h-0 place-items-center border border-white/7 bg-white/[.025] text-center">
    <div><p className="font-mono text-base font-black text-cyan-200">{value}</p><p className="text-[6px] font-black uppercase tracking-[.14em] text-slate-600">{label}</p></div>
  </div>;
}

function StateValue({ label, value, tone = 'default' }) {
  const toneClass = { cyan: 'text-cyan-300', green: 'text-emerald-300', amber: 'text-amber-300', rose: 'text-rose-300', default: 'text-white' }[tone];
  return <div className="grid min-w-0 place-items-center bg-black/10 text-center">
    <div><p className={`font-mono text-lg font-black leading-none ${toneClass}`}>{value ?? 0}</p><p className="mt-1.5 text-[6px] font-black uppercase tracking-[.17em] text-slate-600">{label}</p></div>
  </div>;
}

function QuickCommand({ label, icon: Icon, onClick }) {
  return <button onClick={onClick} className="group grid min-w-0 place-items-center bg-black/10 transition hover:bg-cyan-300/[.06]">
    <div className="text-center"><Icon size={14} className="mx-auto text-slate-600 transition group-hover:text-cyan-300" /><p className="mt-2 text-[6px] font-black uppercase tracking-[.14em] text-slate-500 group-hover:text-slate-200">{label}</p></div>
  </button>;
}

function OverviewSkeleton() {
  return <div className="grid h-full grid-rows-[56px_76px_1fr_112px] gap-3 p-4">
    <Skeleton /><div className="grid grid-cols-6 gap-2">{Array.from({ length: 6 }, (_, index) => <Skeleton key={index} />)}</div>
    <div className="grid min-h-0 grid-cols-[1.6fr_.7fr] gap-3"><Skeleton /><Skeleton /></div>
    <div className="grid grid-cols-3 gap-3"><Skeleton /><Skeleton /><Skeleton /></div>
  </div>;
}
